import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Clock, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { getHoursBankStatus, getHoursBank } from '../../services/api';
import { TaskAreaLabels, TaskAreaColors } from '../../types/task';
import { HoursBankAreaData } from '../../types/hoursBank';

interface HoursUtilizationDashboardProps {
  projectId: string;
  billingMonth: number;
  billingYear: number;
  className?: string;
}

interface AreaHoursData {
  area: string;
  allocatedHours: number;
  usedHours: number;
  availableHours: number;
  utilizationPercentage: number;
  status: 'low' | 'medium' | 'high' | 'critical';
}

export const HoursUtilizationDashboard: React.FC<HoursUtilizationDashboardProps> = ({
  projectId,
  billingMonth,
  billingYear,
  className = ''
}) => {
  const [loading, setLoading] = useState(true);
  const [hoursTrackingEnabled, setHoursTrackingEnabled] = useState(false);
  const [areasData, setAreasData] = useState<AreaHoursData[]>([]);
  const [totals, setTotals] = useState({
    allocatedHours: 0,
    usedHours: 0,
    availableHours: 0,
    utilizationPercentage: 0
  });

  useEffect(() => {
    loadHoursData();
  }, [projectId, billingMonth, billingYear]);

  const loadHoursData = async () => {
    try {
      setLoading(true);

      // Check if hours tracking is enabled
      const statusResponse = await getHoursBankStatus(projectId);
      const enabled = statusResponse.data.data.hoursTrackingEnabled;
      setHoursTrackingEnabled(enabled);

      if (!enabled) {
        setLoading(false);
        return;
      }

      // Use billing period from service order, not start date
      const month = billingMonth;
      const year = billingYear;

      // Load hours bank data
      const hoursResponse = await getHoursBank(projectId, year, month);
      if (hoursResponse.data.data.enabled && hoursResponse.data.data.hoursBank) {
        const bankData = hoursResponse.data.data.hoursBank;
        const { totals: responseTotals } = hoursResponse.data.data;

        // Process areas data
        const areas: AreaHoursData[] = [];
        Object.entries(bankData).forEach(([area, data]) => {
          if (data && typeof data === 'object' && 'allocatedHours' in data) {
            const areaData = data as HoursBankAreaData;
            const utilizationPercentage = areaData.allocatedHours > 0 
              ? Math.round((areaData.usedHours / areaData.allocatedHours) * 100)
              : 0;

            let status: AreaHoursData['status'] = 'low';
            if (utilizationPercentage >= 90) status = 'critical';
            else if (utilizationPercentage >= 75) status = 'high';
            else if (utilizationPercentage >= 50) status = 'medium';

            areas.push({
              area,
              allocatedHours: areaData.allocatedHours,
              usedHours: areaData.usedHours,
              availableHours: areaData.availableHours,
              utilizationPercentage,
              status
            });
          }
        });

        setAreasData(areas);
        setTotals({
          allocatedHours: responseTotals.allocatedHours,
          usedHours: responseTotals.usedHours,
          availableHours: responseTotals.availableHours,
          utilizationPercentage: responseTotals.allocatedHours > 0 
            ? Math.round((responseTotals.usedHours / responseTotals.allocatedHours) * 100)
            : 0
        });
      }
    } catch (error) {
      console.error('Error loading hours data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: AreaHoursData['status']) => {
    switch (status) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-orange-400" />;
      case 'medium':
        return <TrendingUp className="w-4 h-4 text-yellow-400" />;
      default:
        return <CheckCircle className="w-4 h-4 text-green-400" />;
    }
  };


  if (!hoursTrackingEnabled) {
    return null; // Don't show anything if hours tracking is disabled
  }

  if (loading) {
    return (
      <div className={`corporate-card-elevated ${className}`}>
        <div className="corporate-card-header-accent">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-foreground-primary">Banco de Horas Mensal</h3>
          </div>
        </div>
        <div className="corporate-card-content p-6">
          <div className="text-center text-foreground-tertiary">
            Carregando dados do banco de horas...
          </div>
        </div>
      </div>
    );
  }

  if (areasData.length === 0) {
    return (
      <div className={`corporate-card-elevated ${className}`}>
        <div className="corporate-card-header-accent">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-foreground-primary">Banco de Horas Mensal</h3>
          </div>
        </div>
        <div className="corporate-card-content">
          <div className="text-center text-foreground-tertiary">
            Banco de horas não configurado para este período
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`corporate-card-elevated ${className}`}>
      <div className="corporate-card-header-accent">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-foreground-primary">Banco de Horas Mensal</h3>
        </div>
      </div>
      <div className="corporate-card-content p-6 space-y-6">
        {/* Areas Breakdown */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {areasData.map((areaData) => (
              <div key={areaData.area} className="corporate-card p-3" style={{ borderRadius: '12px', border: '1px solid var(--corporate-border-light)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className="corporate-badge corporate-badge-primary text-xs">
                      {TaskAreaLabels[areaData.area as keyof typeof TaskAreaLabels] || areaData.area}
                    </Badge>
                    {getStatusIcon(areaData.status)}
                  </div>
                  <div className="text-xs text-foreground-tertiary">
                    {areaData.utilizationPercentage}%
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-foreground-tertiary">
                    <span>{areaData.usedHours}h usado</span>
                    <span className={areaData.availableHours < 0 ? 'text-red-400' : ''}>
                      {areaData.availableHours}h {areaData.availableHours < 0 ? 'excedido' : 'disponível'}
                    </span>
                  </div>
                  <div className="corporate-progress">
                    <div 
                      className={`corporate-progress-fill ${areaData.status === 'critical' || areaData.availableHours < 0 ? 'error' : areaData.status === 'high' ? 'warning' : ''}`}
                      style={{ width: `${Math.min(areaData.utilizationPercentage, 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-foreground-tertiary text-center">
                    {areaData.allocatedHours}h total contratado
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Warnings */}
        {(areasData.some(area => area.availableHours < 0) || areasData.some(area => area.status === 'critical' || area.status === 'high')) && (
          <div 
            className="corporate-card rounded-lg p-3" 
            style={{ 
              borderColor: areasData.some(area => area.availableHours < 0) ? 'var(--corporate-error)' : 'var(--corporate-warning)', 
              backgroundColor: areasData.some(area => area.availableHours < 0) ? 'var(--corporate-error-light)' : 'var(--corporate-warning-light)' 
            }}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle 
                className="w-4 h-4 mt-0.5" 
                style={{ 
                  color: areasData.some(area => area.availableHours < 0) ? 'var(--corporate-error)' : 'var(--corporate-warning)' 
                }} 
              />
              <div 
                className="text-xs" 
                style={{ 
                  color: areasData.some(area => area.availableHours < 0) ? 'var(--corporate-error-dark)' : 'var(--corporate-warning-dark)' 
                }}
              >
                <div className="font-medium mb-1">Atenção!</div>
                {areasData.filter(area => area.availableHours < 0).length > 0 && (
                  <div className="mb-1">Áreas com horas excedidas: {
                    areasData
                      .filter(area => area.availableHours < 0)
                      .map(area => `${TaskAreaLabels[area.area as keyof typeof TaskAreaLabels] || area.area} (${Math.abs(area.availableHours)}h excedido)`)
                      .join(', ')
                  }</div>
                )}
                {areasData.filter(area => area.status === 'critical' && area.availableHours >= 0).length > 0 && (
                  <div>Áreas com utilização crítica (&gt;90%): {
                    areasData
                      .filter(area => area.status === 'critical' && area.availableHours >= 0)
                      .map(area => TaskAreaLabels[area.area as keyof typeof TaskAreaLabels] || area.area)
                      .join(', ')
                  }</div>
                )}
                {areasData.filter(area => area.status === 'high' && area.availableHours >= 0).length > 0 && (
                  <div>Áreas com alta utilização (&gt;75%): {
                    areasData
                      .filter(area => area.status === 'high' && area.availableHours >= 0)
                      .map(area => TaskAreaLabels[area.area as keyof typeof TaskAreaLabels] || area.area)
                      .join(', ')
                  }</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};